// Data helpers for the Network Map page.

export const BANDS = ['crit', 'high', 'med', 'low', 'unk'];
export const BAND_LABEL = { crit: 'Critical', high: 'High', med: 'Medium', low: 'Low', unk: 'Unscored' };
export const BAND_NAME = { crit: 'CRITICAL', high: 'HIGH', med: 'MEDIUM', low: 'LOW', unk: 'UNSCORED' };

export const bandOf = (risk) => {
  if (risk == null) return 'unk';
  if (risk > 75) return 'crit';
  if (risk > 50) return 'high';
  if (risk > 25) return 'med';
  return 'low';
};

// Colour = risk band. Unscored has no fill; it's drawn with a dashed outline.
export const bandFg = (b) => (b === 'unk' ? 'var(--ink-faint)' : `var(--sev-${b}-fg)`);
export const bandBg = (b) => (b === 'unk' ? 'transparent' : `var(--sev-${b}-bg)`);

// Shape = device type.
export const kindOf = (deviceType) => {
  const t = (deviceType || '').toLowerCase();
  if (t === 'server' || t === 'linux' || t === 'windows') return 'server';
  if (t === 'network' || t === 'network_device' || t === 'router' || t === 'switch' || t === 'firewall') return 'network';
  if (t === 'workstation' || t === 'endpoint' || t === 'iot' || t === 'printer' || t === 'camera') return 'endpoint';
  return 'unknown';
};

export const TYPE_LABEL = {
  server: 'Server', workstation: 'Workstation', network: 'Network device', network_device: 'Network device',
  iot: 'IoT device', endpoint: 'Endpoint', unknown: 'Unknown device',
};
export const typeLabel = (t) => TYPE_LABEL[(t || '').toLowerCase()] || (t ? t.charAt(0).toUpperCase() + t.slice(1) : 'Unknown device');

export const critLabel = (score) => (score >= 9 ? 'CRITICAL' : score >= 7 ? 'HIGH' : score >= 4 ? 'MEDIUM' : 'LOW');

const parseTs = (iso) => {
  if (!iso) return null;
  const d = new Date(iso.includes('T') ? iso : `${iso.replace(' ', 'T')}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
};

// Online < 15 min since last heartbeat, degraded < 60 min, otherwise offline.
export const statusOf = (asset) => {
  if (asset.discovered) return { key: 'discovered', label: 'Discovered', dot: 'var(--ink-faint)' };
  const d = parseTs(asset.last_seen);
  if (!d) return { key: 'offline', label: 'Offline', dot: 'var(--ink-faint)' };
  const mins = (Date.now() - d.getTime()) / 60000;
  if (mins < 15) return { key: 'online', label: 'Online', dot: 'var(--sev-good-fg)' };
  if (mins < 60) return { key: 'degraded', label: 'Degraded', dot: 'var(--sev-med-fg)' };
  return { key: 'offline', label: 'Offline', dot: 'var(--ink-faint)' };
};

export const ago = (iso) => {
  const d = parseTs(iso);
  if (!d) return '—';
  const s = Math.max(0, Math.round((Date.now() - d.getTime()) / 1000));
  if (s < 60) return `${s} s`;
  if (s < 3600) return `${Math.round(s / 60)} min`;
  if (s < 86400) return `${Math.round(s / 3600)} h`;
  return `${Math.round(s / 86400)} d`;
};

export const shortDate = (iso) => {
  const d = parseTs(iso);
  return d ? d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'Asia/Kuala_Lumpur' }) : '—';
};

// Normalise /api/servers rows (+ risk scores) into what the page renders.
// riskMap values are /api/dashboard/risk rows (or bare scores).
export const toAsset = (s, riskMap) => {
  const discovered = s.lifecycle_state === 'DISCOVERED';
  const r = riskMap.has(s.id) ? riskMap.get(s.id) : null;
  const row = r != null && typeof r === 'object' ? r : null;
  // A discovered device with no alerts has only the formula's floor (importance + confidence bonus),
  // the same for every one of them, so it is shown as unscored.
  const alerted = row ? (row.active_ioc_count || 0) + (row.active_cve_count || 0) + (row.active_finding_count || 0) > 0 : r != null;
  const risk = discovered && !alerted ? null : row ? row.risk_score ?? null : r;
  const unnamed = !s.hostname || s.hostname === s.ip_address;
  return {
    id: s.id,
    host: unnamed ? (s.ip_address || 'unknown') : s.hostname,
    unnamed,
    hostname: s.hostname,
    ip: s.ip_address || '',
    deviceType: s.device_type || s.os_type || 'unknown',
    kind: kindOf(s.device_type || s.os_type),
    os: s.os_info || null,
    location: s.location || '',
    criticality: s.criticality_score ?? 5,
    internetFacing: !!s.is_internet_facing,
    mac: s.mac_address || null,
    source: s.source,
    discovered,
    discoveredBy: s.discovered_by || null,
    last_seen: s.last_seen,
    created_at: s.created_at,
    risk,
    band: bandOf(risk),
    ports: Array.isArray(s.discovered_ports) ? s.discovered_ports : [],
    findings: s.open_findings || null,
    vulns: s.open_vulns || 0,
    vendor: s.hardware_vendor || null,
    breakdown: row?.breakdown || null,
    iocs: row?.active_ioc_count || 0,
  };
};

// An agent asset is "silent" once its heartbeat is late (degraded or offline).
export const hasAgent = (a) => !a.discovered && a.source !== 'manual';
export const isSilent = (a) => hasAgent(a) && statusOf(a).key !== 'online';

// Ports worth calling out: remote access, file sharing and databases, plus SSH
// when the asset faces the internet.
const RISKY_PORTS = new Set([21, 23, 445, 1433, 3306, 3389, 5432, 5900, 6379, 9200, 27017]);
export const isRiskyPort = (port, internetFacing) => RISKY_PORTS.has(port) || (internetFacing && port === 22);

// The four parts of a risk score (/api/dashboard/risk breakdown), largest first.
export const riskParts = (a) => {
  const b = a.breakdown;
  if (!b) return [];
  const finds = a.findings?.total || 0;
  return [
    { key: 'threats', label: `Threats · ${finds} finding${finds === 1 ? '' : 's'}${a.iocs ? `, ${a.iocs} IoC${a.iocs === 1 ? '' : 's'}` : ''}`, value: b.threats, color: 'var(--sev-crit-fg)' },
    { key: 'vulns', label: `${a.vulns} open CVE${a.vulns === 1 ? '' : 's'}`, value: b.vulnerabilities, color: 'var(--sev-high-fg)' },
    { key: 'importance', label: `Criticality ${a.criticality}/10`, value: b.importance, color: 'var(--sev-low-fg)' },
    { key: 'confidence', label: 'Recent & corroborated', value: b.confidence_bonus, color: 'var(--accent)' },
  ].filter(p => p.value > 0);
};

// ── Quick filters (the summary tiles) ─────────────────────────────────────
export const QUICK = ['crit', 'silent', 'internet', 'review'];

export const reachesInternet = (edges) => new Set((edges || []).filter(e => e.kind === 'internet').map(e => e.source));

export const quickMatch = (key, a, internetIds) => {
  if (key === 'crit') return a.band === 'crit';
  if (key === 'silent') return isSilent(a);
  if (key === 'internet') return internetIds.has(a.id) || a.internetFacing;
  if (key === 'review') return a.discovered;
  return true;
};

// Private addresses agents connected to that no asset has, one row per IP.
export const unknownAddresses = (edges) => {
  const byIp = new Map();
  (edges || []).filter(e => e.kind === 'unmanaged').forEach(e => {
    (e.top_peers || []).forEach(p => {
      const row = byIp.get(p.ip) || { ip: p.ip, observations: 0, sources: [] };
      row.observations += p.observations;
      if (!row.sources.includes(e.source)) row.sources.push(e.source);
      byIp.set(p.ip, row);
    });
  });
  return [...byIp.values()].sort((x, y) => y.observations - x.observations);
};

// Band of the worst open finding, or null when there are none.
export const worstFinding = (f) => {
  if (!f || !f.total) return null;
  if (f.critical) return 'crit';
  if (f.high) return 'high';
  if (f.medium) return 'med';
  return 'low';
};

// ── Connections (/api/network/edges) ──────────────────────────────────────
// Asset edges are directed (the agent that saw the connection is the source);
// the inspector lists each peer once, so merge both directions.
export const linkPairs = (edges) => {
  const pairs = new Map();
  (edges || []).filter(e => e.kind === 'asset').forEach(e => {
    const [a, b] = e.source < e.target ? [e.source, e.target] : [e.target, e.source];
    const key = `${a}|${b}`;
    const p = pairs.get(key) || { a, b, observations: 0, last_seen: e.last_seen };
    p.observations += e.observations;
    if (e.last_seen > p.last_seen) p.last_seen = e.last_seen;
    pairs.set(key, p);
  });
  return [...pairs.values()];
};

// Everything one asset talks to: other assets (either direction), plus its
// internet and unmanaged aggregates when its agent reported them.
export const connectionsOf = (edges, id) => {
  const peers = linkPairs(edges)
    .filter(p => p.a === id || p.b === id)
    .map(p => ({ id: p.a === id ? p.b : p.a, observations: p.observations, last_seen: p.last_seen }))
    .sort((x, y) => y.observations - x.observations);
  const own = (kind) => (edges || []).find(e => e.kind === kind && e.source === id) || null;
  return { peers, internet: own('internet'), unmanaged: own('unmanaged') };
};
